import React, { useState } from 'react';
import { FormikProps } from 'formik';

import usePrefix from 'utils/usePrefix';
import { PostUserRequest } from 'utils/api/users';
import { userRoles } from 'utils/userRoles';
import { getApiUrl, api, ResponseError } from 'utils/api';
import { notificationTypes } from 'utils/constants';

import { ActionTypes } from 'App/types';
import { useApp } from 'App';

import Button from 'components/Button';
import DetailsTitle from 'components/DetailsTitle';
import ConfirmModal from 'components/ConfirmModal';
import LanguageSelect from 'components/LanguageSelect';
import RoleSelect from 'components/RoleSelect';
import FlexDiv from 'components/FlexDiv';
import { FormGroupStyled } from 'components/Forms/styles';
import InputField from 'components/InputField';
import ToggleSwitch from 'components/ToggleSwitch';
import { StyledFormField } from 'pages/Schools/School/styles';

import {
  Content,
  ButtonsContainer,
} from './styles';

const url = getApiUrl('/users');

interface Props {
  onClose: () => void;
  userId?: number;
  isActive?: boolean;
  fetchData: () => Promise<any>;
}

const DetailsForm: React.FC<
  Pick<FormikProps<PostUserRequest>, 'errors' | 'touched' | 'isSubmitting' | 'values'> & Props
> = ({ errors, touched, isSubmitting, onClose, values, userId, isActive, fetchData, ...props }) => {
  const [, dispatch] = useApp();

  const [isModalOpen, setModal] = useState(false);

  const t = usePrefix('General');
  const userName = `${values.first_name} ${values.last_name}`;
  // eslint-disable-next-line no-magic-numbers
  const isUserPlaceholder = values.first_name.length < 1 && values.last_name.length < 1;

  const handleUserStatus = async () => {
    try {
      const response = await api(`${url}/${userId}/${isActive ? 'not-active' : 'active'}`, {
        method: 'PATCH',
        payload: {
          ...values,
        },
      });

      if (response) {
        await fetchData();
        onClose();
      }
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  return (
    <Content {...props} autoComplete="off">
      <FlexDiv justifyContent="space-between" alignItems="center">
        <DetailsTitle
          title={isUserPlaceholder ? t('user_placeholder') : userName}
          empty={isUserPlaceholder}
        />
        <ButtonsContainer alignItems="center">
          {!!userId && (
            <Button onClick={() => setModal(true)} color="red">
              {isActive ? t('deactivate_user') : t('activate_user')}
            </Button>
          )}
          <Button onClick={onClose} color="link">
            {t('dont_save_changes')}
          </Button>
          <Button label={t('save_changes')} type="submit" disabled={isSubmitting} />
        </ButtonsContainer>
      </FlexDiv>
      <FormGroupStyled>
        <RoleSelect name="role" defaultValue={userRoles.intervener} />
        <LanguageSelect name="user_language" defaultValue={values.user_language} />
        <InputField
          name="first_name"
          error={touched.first_name && errors.first_name}
          label={t('firstname')}
        />
        <InputField
          name="last_name"
          error={touched.last_name && errors.last_name}
          label={t('lastname')}
        />
        <InputField
          name="email"
          error={touched.email && errors.email}
          label={t('email')}
          type="email"
        />
        <InputField
          name="phone"
          error={touched.phone && errors.phone}
          label={t('phone_no')}
          type="tel"
        />
        {values.role === userRoles.admin ? (
          <StyledFormField label={t('resql_expert')} margin={'0'}>
            <ToggleSwitch name="handles_resql_expert" checked={values.handles_resql_expert} />
          </StyledFormField>
        ) : null}
      </FormGroupStyled>
      {isModalOpen && (
        <ConfirmModal
          onCancel={() => setModal(false)}
          onConfirm={handleUserStatus}
          title={t('confirmation')}
          text={
            isActive
              ? t('deactivate_user_confirm', { userName })
              : t('activate_user_confirm', { userName })
          }
        />
      )}
    </Content>
  );
};

export default DetailsForm;
